import axios from 'axios';

// Use relative path if VITE_API_BASE_URL is not set (for Firebase Hosting rewrites)
// Use relative path if VITE_API_BASE_URL is not set (for Firebase Hosting rewrites)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://backend.wealthplusindia.com';

const AUTH_TOKEN_KEY = import.meta.env.VITE_AUTH_TOKEN_KEY || 'token';
const MAX_RETRIES = parseInt(import.meta.env.VITE_API_RETRY_COUNT) || 3;
const RETRY_DELAY = parseInt(import.meta.env.VITE_API_RETRY_DELAY) || 1000;
const TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT) || 10000;



// Create axios instance with default config
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: TIMEOUT,
});

// Retry delay helper
const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Create retry interceptor
api.interceptors.response.use(undefined, async (err) => {
  const { config, message } = err;
  if (!config || !config.retry) {
    return Promise.reject(err);
  }

  config.retryCount = config.retryCount || 0;

  if (config.retryCount >= config.retry) {
    return Promise.reject(err);
  }

  // Retry on network errors but NOT on 429 (too many requests)
  if (!err.response) {
    config.retryCount += 1;

    // Implement exponential backoff
    const backoffDelay = RETRY_DELAY * Math.pow(2, config.retryCount - 1);

    await wait(backoffDelay);
    return api(config);
  }

  return Promise.reject(err);
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Get token from cookies
    const token = getCookie(AUTH_TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for successful responses
api.interceptors.response.use(
  (response) => {
    // Check if response exists and has data
    if (!response || !response.data) {
      throw new Error('Invalid response received from server');
    }

    // Handle different response formats
    const { data, ok, error } = response.data;

    // If the response indicates an error
    if (error || ok === false) {
      throw new Error(error || 'Request failed');
    }

    // For auth/me endpoint, return the data directly
    if (response.config?.url?.includes('/auth/me')) {
      return data || response.data;
    }

    // Return the data property if it exists, otherwise return the whole response.data
    return data !== undefined ? data : response.data;
  },
  async (error) => {
    // Handle network errors (no response)
    if (!error.response) {
      console.error('Network Error:', error.message);
      throw new Error('Network error - please check your connection');
    }

    const { status, data } = error.response;

    // Handle different error status codes
    switch (status) {
      case 401:
        // Clear auth token
        document.cookie = `${AUTH_TOKEN_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        window.location.href = '/login';
        throw new Error('Authentication failed');

      case 403:
        throw new Error('Access denied');

      case 404:
        throw new Error('Resource not found');

      case 429:
        // For 429 errors, don't retry automatically as it will make it worse
        // Let the user handle it manually
        throw new Error('Too many requests - please wait a moment and try again');

      case 500:
        console.error('Server Error:', error);
        throw new Error('Internal server error');

      default:
        throw new Error(data?.message || error.message || 'An unexpected error occurred');
    }
  }
);

// Helper function to get cookie value
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

// Default request config with retry options
const defaultConfig = {
  retry: MAX_RETRIES,
  retryDelay: RETRY_DELAY
};

// API Methods
export const authAPI = {
  getCurrentUser: async () => {

    try {
      const token = getCookie(AUTH_TOKEN_KEY);

      if (!token) {

        return null;
      }

      const result = await api.get('/api/auth/me', defaultConfig);

      return result;
    } catch (error) {
      console.error('Get current user failed:', error.message);

      // Don't throw on 401/403, just return null
      if (error.response?.status === 401 || error.response?.status === 403) {

        return null;
      }
      throw error;
    }
  },
  logout: async () => {
    try {
      // Clear auth token
      document.cookie = `${AUTH_TOKEN_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    } catch (error) {
      console.error('Logout failed:', error.message);
    }
  },
  login: async (credentials) => {
    try {

      const response = await api.post('/api/auth/login', credentials, defaultConfig);

      if (response?.accessToken) {
        // Set the access token in cookie
        document.cookie = `${AUTH_TOKEN_KEY}=${response.accessToken}; path=/; max-age=86400; SameSite=Lax`;
        // Also set refresh token if available
        if (response.refreshToken) {
          document.cookie = `refreshToken=${response.refreshToken}; path=/; max-age=604800; SameSite=Lax`;
        }
      }
      return response;
    } catch (error) {
      console.error('Login failed:', error.message);
      console.error('Login error details:', error);
      throw new Error(error.message || 'Login failed');
    }
  }
};

export const usersAPI = {
  getUsers: async (params = {}) => {
    const response = await api.get('/api/users', { params });
    return response;
  },
  createUser: async (userData) => {
    const response = await api.post('/api/users', userData);
    return response;
  },
  updateUser: async (userId, userData) => {
    const response = await api.put(`/api/users/${userId}`, userData);
    return response;
  },
  deleteUser: async (userId) => {
    const response = await api.delete(`/api/users/${userId}`);
    return response;
  }
};

export const policiesAPI = {
  getPolicies: async (params = {}) => {
    const response = await api.get('/api/policies', { params });
    return response;
  },
  createPolicy: async (policyData) => {
    const response = await api.post('/api/policies', policyData);
    return response;
  },
  updatePolicy: async (policyId, policyData) => {
    const response = await api.put(`/api/policies/${policyId}`, policyData);
    return response;
  },
  deletePolicy: async (policyId) => {
    const response = await api.delete(`/api/policies/${policyId}`);
    return response;
  }
};

export const commissionsAPI = {
  getCommissions: async (params = {}) => {
    const response = await api.get('/api/commissions', { params });
    return response;
  },
  getSettings: async () => {
    const response = await api.get('/api/commissions/settings');
    return response;
  },
  updateSettings: async (settings) => {
    const response = await api.post('/api/commissions/settings', settings);
    return response;
  },
  recalculate: async (data) => {
    const response = await api.post('/api/commissions/recalculate', data);
    return response;
  },
  processPayment: async (commissionId, transactionData) => {
    const response = await api.post(`/api/commissions/${commissionId}/process-payment`, transactionData);
    return response;
  }
};

export const postersAPI = {
  getPosters: async (params = {}) => {
    const response = await api.get('/api/posters', { params });
    return response;
  },
  uploadPoster: async (formData) => {
    const response = await api.post('/api/posters/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },
  updatePoster: async (posterId, updateData) => {
    const response = await api.put(`/api/posters/${posterId}`, updateData);
    return response;
  },
  deletePoster: async (posterId) => {
    const response = await api.delete(`/api/posters/${posterId}`);
    return response;
  },
  getCategories: async () => {
    const response = await api.get('/api/posters/categories');
    return response;
  },
  createCategory: async (categoryData) => {
    const response = await api.post('/api/posters/categories', categoryData);
    return response;
  }
};